import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Plus, Pencil, Trash2, Upload, FileSpreadsheet, Save, FileDown,
  Clock, CalendarRange, Calculator, BadgeEuro, Download, FileText, Eye,
  Pencil as EditIcon, Search,
} from "lucide-react";
import { formatDate } from "@/lib/dates";
import { DateInputIT } from "@/components/ui/date-input-it";
import { propertyTone, findPropertyForKw } from "@/lib/propertyTone";
import { errMsg } from "@/lib/errors";

// --- Helpers for inline time-editing & recompute ---
const _hmToHours = (hm) => {
  if (!hm || !/^\d{1,2}:\d{2}$/.test(hm)) return null;
  const [h, m] = hm.split(":").map(Number);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h + m / 60;
};

// Compute hours from "HH:MM" start/end (handles overnight: end < start ⇒ +24)
const computeShiftHours = (start, end) => {
  const s = _hmToHours(start);
  const e = _hmToHours(end);
  if (s == null || e == null) return 0;
  let d = e - s;
  if (d < 0) d += 24;
  if (d > 16) return 0; // implausible
  return Math.round(d * 100) / 100;
};

// Recalculate one entry given its date/start/end + rates
const recomputeEntry = (entry, rateWeekday, rateHoliday) => {
  const hours = computeShiftHours(entry.start, entry.end);
  const rate = entry.is_holiday ? rateHoliday : rateWeekday;
  const pay = Math.round(hours * rate * 100) / 100;
  // clear "valore anomalo" note if hours are now plausible
  let note = entry.note;
  if (note && /valore anomalo|ricalcolato/.test(note) && hours > 0) {
    note = note.replace(/(·\s*)?(valore anomalo[^·]*|ricalcolato[^·]*)/g, "").trim();
    note = note.replace(/^·\s*/, "").replace(/\s*·\s*$/, "").trim() || null;
  }
  return { ...entry, hours, rate, pay, note };
};

// Recompute aggregate totals for an array of entries
const recomputeTotals = (entries, rateWeekday, rateHoliday) => {
  let hw = 0, hh = 0;
  entries.forEach((e) => {
    if (e.is_holiday) hh += Number(e.hours || 0);
    else hw += Number(e.hours || 0);
  });
  hw = Math.round(hw * 100) / 100;
  hh = Math.round(hh * 100) / 100;
  const pw = Math.round(hw * rateWeekday * 100) / 100;
  const ph = Math.round(hh * rateHoliday * 100) / 100;
  return {
    hours_weekday: hw,
    hours_holiday: hh,
    hours_total: Math.round((hw + hh) * 100) / 100,
    pay_weekday: pw,
    pay_holiday: ph,
    total_pay: Math.round((pw + ph) * 100) / 100,
    entries_count: entries.length,
  };
};

const EMPTY_EMP = {
  name: "",
  role: "Pulizie",
  hourly_rate_weekday: 7,
  hourly_rate_holiday: 9,
  client_filter: "Emmanuel",
  client_filters: ["Emmanuel"],
  active: true,
  notes: "",
};

export default function EmployeesTab() {
  const [employees, setEmployees] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  // Multi-struttura
  const [properties, setProperties] = useState([]);
  const [tsPropertyFilter, setTsPropertyFilter] = useState("all");
  // Storico timbrature: text search + date range
  const [tsSearch, setTsSearch] = useState("");
  const [tsFrom, setTsFrom] = useState("");
  const [tsTo, setTsTo] = useState("");

  // Memoized filtered timesheets for the storico table
  const filteredTimesheets = (() => {
    return timesheets.filter((t) => {
      if (tsPropertyFilter !== "all") {
        const cf = (t.client_filter || "").toLowerCase();
        if (!cf.includes(tsPropertyFilter.toLowerCase())) return false;
      }
      if (tsSearch.trim()) {
        const q = tsSearch.toLowerCase();
        const hay = `${t.employee_name || ""} ${t.filename || ""} ${t.client_filter || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (tsFrom && t.period_end && t.period_end < tsFrom) return false;
      if (tsTo && t.period_start && t.period_start > tsTo) return false;
      return true;
    });
  })();

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY_EMP);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadEmpId, setUploadEmpId] = useState("");
  // Multi-struttura: which property's keyword to use as filter override
  const [uploadPropertyId, setUploadPropertyId] = useState(""); // "" = use employee default filters
  const [uploadFile, setUploadFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [e, t, p] = await Promise.all([
        api.get("/admin/employees"),
        api.get("/admin/employees/timesheets"),
        api.get("/admin/properties").catch(() => ({ data: { properties: [] } })),
      ]);
      setEmployees(e.data.employees || []);
      setTimesheets(t.data.timesheets || []);
      setProperties(p.data.properties || []);
    } catch {
      toast.error("Errore caricamento dipendenti");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const startNew = () => { setEditing({ ...EMPTY_EMP }); setEditOpen(true); };
   
  const startEdit = (e) => {
    // Backward-compat: if employee has only client_filter (string) but no client_filters list, init list from it.
    const filters = Array.isArray(e.client_filters) && e.client_filters.length
      ? e.client_filters
      : (e.client_filter ? [e.client_filter] : []);
    setEditing({ ...e, client_filters: filters });
    setEditOpen(true);
  };

  const saveEmployee = async () => {
    if (!editing.name?.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      const filters = (editing.client_filters || []).filter((x) => x && x.trim());
      const payload = {
        ...editing,
        client_filters: filters,
        // Keep legacy single client_filter in sync (first item) for backward compat
        client_filter: filters[0] || editing.client_filter || "",
        hourly_rate_weekday: Number(editing.hourly_rate_weekday) || 0,
        hourly_rate_holiday: Number(editing.hourly_rate_holiday) || 0,
      };
      if (editing.id) await api.put(`/admin/employees/${editing.id}`, payload);
      else await api.post("/admin/employees", payload);
      toast.success("Dipendente salvato");
      setEditOpen(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Eliminare questo dipendente?")) return;
    try {
      await api.delete(`/admin/employees/${id}`);
      toast.success("Eliminato");
      refresh();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const openUploadFor = (empId) => {
    setUploadEmpId(empId);
    setUploadFile(null);
    setPreview(null);
    setUploadOpen(true);
  };

  const doPreview = async (clientOverride = undefined) => {
    if (!uploadFile || !uploadEmpId) return;
    setParsing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      // Build query: if override provided, pass it (use "" to disable filter)
      // If no explicit override but a property is chosen in the upload dialog, use its keyword.
      let effectiveOverride = clientOverride;
      if (effectiveOverride === undefined) {
        if (uploadPropertyId === "none") effectiveOverride = "";
        else if (uploadPropertyId && uploadPropertyId !== "auto") {
          const p = properties.find((pp) => pp.id === uploadPropertyId);
          if (p) effectiveOverride = p.client_filter_keyword || p.name.split(" ").pop();
        }
      }
      let url = `/admin/employees/timesheet/preview?employee_id=${uploadEmpId}`;
      if (effectiveOverride !== undefined) {
        url += `&client_filter_override=${encodeURIComponent(effectiveOverride)}`;
      }
      const res = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
      const n = res.data.totals.entries_count;
      if (n === 0 && (res.data.skipped_other_client_rows || 0) > 0) {
        const clients = (res.data.detected_clients || []).map((c) => c.name).join(", ");
        toast.warning(
          `Nessuna riga trovata col filtro "${res.data.client_filter || "—"}". ` +
            `Clienti nel file: ${clients || "—"}. Scegli un cliente o disattiva il filtro.`,
          { duration: 8000 },
        );
      } else {
        toast.success(`Analizzati ${n} timbri`);
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore analisi file"));
    } finally {
      setParsing(false);
    }
  };

  // Inline edit a preview entry (Inizio/Fine) and recompute hours, pay & totals.
  const editPreviewEntry = (index, field, value) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const entries = prev.entries.slice();
      const updated = { ...entries[index], [field]: value };
      entries[index] = recomputeEntry(
        updated,
        prev.hourly_rate_weekday,
        prev.hourly_rate_holiday,
      );
      return {
        ...prev,
        entries,
        totals: recomputeTotals(
          entries,
          prev.hourly_rate_weekday,
          prev.hourly_rate_holiday,
        ),
      };
    });
  };

  // Delete a row from the preview
  const deletePreviewEntry = (index) => {
    if (!window.confirm("Eliminare questo timbro?")) return;
    setPreview((prev) => {
      if (!prev) return prev;
      const entries = prev.entries.filter((_, i) => i !== index);
      return {
        ...prev,
        entries,
        totals: recomputeTotals(
          entries,
          prev.hourly_rate_weekday,
          prev.hourly_rate_holiday,
        ),
      };
    });
    toast.success("Timbro rimosso");
  };

  // Same for an already-saved timesheet shown in the View dialog.
  const editViewEntry = (index, field, value) => {
    setViewing((prev) => {
      if (!prev) return prev;
      const entries = (prev.entries || []).slice();
      const updated = { ...entries[index], [field]: value };
      entries[index] = recomputeEntry(
        updated,
        prev.hourly_rate_weekday,
        prev.hourly_rate_holiday,
      );
      return {
        ...prev,
        entries,
        totals: recomputeTotals(
          entries,
          prev.hourly_rate_weekday,
          prev.hourly_rate_holiday,
        ),
      };
    });
  };

  // Delete a row from the View dialog (not yet persisted - hit Save to apply)
  const deleteViewEntry = (index) => {
    if (!window.confirm("Eliminare questo timbro? Premi poi 'Salva modifiche' per renderlo permanente.")) return;
    setViewing((prev) => {
      if (!prev) return prev;
      const entries = (prev.entries || []).filter((_, i) => i !== index);
      return {
        ...prev,
        entries,
        totals: recomputeTotals(
          entries,
          prev.hourly_rate_weekday,
          prev.hourly_rate_holiday,
        ),
      };
    });
  };

  // Persist View-dialog changes back to MongoDB.
  const saveViewChanges = async () => {
    if (!viewing?.id) return;
    try {
      await api.put(`/admin/employees/timesheets/${viewing.id}`, {
        entries: viewing.entries,
        totals: viewing.totals,
      });
      toast.success("Modifiche salvate");
      setViewOpen(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    }
  };

  const doSave = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const payload = {
        employee_id: uploadEmpId,
        filename: uploadFile?.name,
        employee_name: preview.employee_name,
        period_start: preview.period_start,
        period_end: preview.period_end,
        hourly_rate_weekday: preview.hourly_rate_weekday,
        hourly_rate_holiday: preview.hourly_rate_holiday,
        client_filter: preview.client_filter,
        entries: preview.entries,
        totals: preview.totals,
        notes: null,
      };
      await api.post("/admin/employees/timesheet/import", payload);
      toast.success("Timbratura salvata");
      setUploadOpen(false);
      setPreview(null);
      setUploadFile(null);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  const deleteTimesheet = async (id) => {
    if (!window.confirm("Eliminare questa timbratura?")) return;
    try {
      await api.delete(`/admin/employees/timesheets/${id}`);
      toast.success("Eliminata");
      refresh();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const downloadTs = async (id, fmt) => {
    try {
      const res = await api.get(`/admin/export/timesheet/${id}?fmt=${fmt}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `timbratura.${fmt}`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Errore download");
    }
  };

  const viewTimesheet = async (id) => {
    try {
      const res = await api.get(`/admin/employees/timesheets/${id}`);
      setViewing(res.data);
      setViewOpen(true);
    } catch {
      toast.error("Errore caricamento timbratura");
    }
  };

  const downloadPreview = async (fmt) => {
    if (!preview) return;
    try {
      const res = await api.post(
        `/admin/export/timesheet-preview?fmt=${fmt}`,
        preview,
        { responseType: "blob" },
      );
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `anteprima_timbrature.${fmt}`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Errore export anteprima");
    }
  };

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="employees-tab">
      {/* HEADER */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
              <Users className="w-5 h-5" /> Dipendenti & Timbrature
            </h3>
            <p className="text-xs text-[#635F59] mt-1">
              Gestisci i collaboratori (es. pulizie), carica i file Excel
              <i> apprilevazionepresenze</i> e calcola automaticamente la paga
              (festivi: weekend <b>sabato e domenica</b> + festività italiane &quot;rosse&quot;).
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={startNew} data-testid="emp-add" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
              <Plus className="w-4 h-4 mr-2" />Nuovo dipendente
            </Button>
          </div>
        </div>
      </div>

      {/* EMPLOYEES LIST */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#635F59]">Caricamento...</div>
        ) : employees.length === 0 ? (
          <div className="p-10 text-center text-[#635F59]" data-testid="emp-empty">
            Nessun dipendente. Aggiungine uno per iniziare a importare le timbrature.
          </div>
        ) : (
          <Table data-testid="emp-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>€/h feriale</TableHead>
                <TableHead>€/h festivo</TableHead>
                <TableHead>Strutture</TableHead>
                <TableHead>Attivo</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((e) => {
                const filters = Array.isArray(e.client_filters) && e.client_filters.length
                  ? e.client_filters
                  : (e.client_filter ? [e.client_filter] : []);
                return (
                <TableRow key={e.id} data-testid={`emp-row-${e.id}`}>
                  <TableCell className="font-medium text-[#1E3A5F]">{e.name}</TableCell>
                  <TableCell className="text-sm text-[#635F59]">{e.role || "—"}</TableCell>
                  <TableCell>€ {Number(e.hourly_rate_weekday).toFixed(2)}</TableCell>
                  <TableCell>€ {Number(e.hourly_rate_holiday).toFixed(2)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      {filters.length === 0 ? <span className="text-xs text-[#635F59]">—</span> : filters.map((kw) => {
                        const p = findPropertyForKw(properties, kw);
                        const color = propertyTone(p);
                        const label = p?.name || kw;
                        return (
                          <span
                            key={kw}
                            className="text-xs font-semibold whitespace-nowrap"
                            style={{ color }}
                            data-testid={`emp-filter-${e.id}-${kw}`}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell>
                    {e.active
                      ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" variant="outline">Sì</Badge>
                      : <Badge className="bg-slate-100 text-slate-700 border-slate-300" variant="outline">No</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openUploadFor(e.id)} data-testid={`emp-import-${e.id}`} className="text-[#AC5D45] border-[#AC5D45]">
                        <Upload className="w-3.5 h-3.5 mr-1" />Importa Excel
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => startEdit(e)} data-testid={`emp-edit-${e.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => deleteEmployee(e.id)} data-testid={`emp-del-${e.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* TIMESHEETS HISTORY */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl">
        <div className="p-4 border-b border-[#E8E2D9] flex items-center gap-2 flex-wrap">
          <FileSpreadsheet className="w-4 h-4 text-[#1E3A5F]" />
          <span className="font-semibold text-[#1E3A5F]">Storico timbrature importate</span>
          {properties.length > 1 && (
            <div className="ml-3 flex items-center gap-1 flex-wrap" data-testid="ts-property-filter">
              <button
                type="button"
                onClick={() => setTsPropertyFilter("all")}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${tsPropertyFilter === "all" ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white text-[#635F59] border-[#E8E2D9] hover:bg-[#FDFBF7]"}`}
                data-testid="ts-filter-all"
              >
                Tutte
              </button>
              {properties.map((p) => {
                const kw = p.client_filter_keyword || p.name.split(" ").pop();
                const isOn = tsPropertyFilter === kw;
                const tone = propertyTone(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setTsPropertyFilter(kw)}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition ${isOn ? "text-white" : "bg-white hover:bg-[#FDFBF7]"}`}
                    style={isOn
                      ? { backgroundColor: tone, borderColor: tone }
                      : { color: tone, borderColor: tone }}
                    data-testid={`ts-filter-${p.slug || p.id}`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          )}
          <span className="text-xs text-[#635F59] ml-auto">
            {(() => {
              const visible = filteredTimesheets;
              return `${visible.length} ${visible.length === 1 ? "salvata" : "salvate"}${(tsPropertyFilter !== "all" || tsSearch || tsFrom || tsTo) ? ` · filtrate da ${timesheets.length}` : ""}`;
            })()}
          </span>
        </div>

        {/* Search + date range filter bar */}
        <div className="px-3 py-2.5 border-b border-[#E8E2D9] bg-[#FDFBF7]/40 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[180px] relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#635F59]" />
            <Input
              value={tsSearch}
              onChange={(e) => setTsSearch(e.target.value)}
              placeholder="Cerca dipendente, file..."
              className="h-9 pl-8 text-xs"
              data-testid="ts-search"
            />
          </div>
          <DateInputIT value={tsFrom}
            onChange={(e) => setTsFrom(e.target.value)}
            className="h-9 text-xs w-[140px]"
            data-testid="ts-from"
            title="Periodo da"
          />
          <DateInputIT value={tsTo}
            onChange={(e) => setTsTo(e.target.value)}
            className="h-9 text-xs w-[140px]"
            data-testid="ts-to"
            title="Periodo a"
          />
          {(tsSearch || tsFrom || tsTo) && (
            <button
              type="button"
              onClick={() => { setTsSearch(""); setTsFrom(""); setTsTo(""); }}
              className="text-[11px] text-[#AC5D45] hover:underline"
              data-testid="ts-clear-filters"
            >
              Pulisci filtri
            </button>
          )}
        </div>
        {filteredTimesheets.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#635F59]">
            {timesheets.length === 0 ? "Nessuna timbratura importata." : "Nessuna timbratura corrisponde ai filtri."}
          </div>
        ) : (
          <Table data-testid="ts-table">
            <TableHeader>
              <TableRow>
                <TableHead>Dipendente</TableHead>
                <TableHead>Struttura</TableHead>
                <TableHead>Periodo</TableHead>
                <TableHead className="text-right">Ore feriali</TableHead>
                <TableHead className="text-right">Ore festive</TableHead>
                <TableHead className="text-right">Totale ore</TableHead>
                <TableHead className="text-right">Paga € totale</TableHead>
                <TableHead className="text-right">File</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTimesheets.map((t) => {
                const tsProp = findPropertyForKw(properties, t.client_filter) ||
                  properties.find((p) => {
                    const kw = (p.client_filter_keyword || p.name.split(" ").pop() || "").toLowerCase();
                    return kw && (t.client_filter || "").toLowerCase().includes(kw);
                  });
                return (
                <TableRow key={t.id} data-testid={`ts-row-${t.id}`}>
                  <TableCell className="font-medium">{t.employee_name || empName(t.employee_id)}</TableCell>
                  <TableCell>
                    {tsProp ? (
                      <span
                        className="text-xs font-semibold whitespace-nowrap"
                        style={{ color: propertyTone(tsProp) }}
                      >
                        {tsProp.name}
                      </span>
                    ) : (
                      <span className="text-xs text-[#635F59]">{t.client_filter || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap font-mono">
                    {t.period_start ? formatDate(t.period_start) : "—"} → {t.period_end ? formatDate(t.period_end) : "—"}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">{(t.totals?.hours_weekday ?? 0).toFixed(2)} h</TableCell>
                  <TableCell className="text-right whitespace-nowrap">{(t.totals?.hours_holiday ?? 0).toFixed(2)} h</TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap">{(t.totals?.hours_total ?? 0).toFixed(2)} h</TableCell>
                  <TableCell className="text-right font-semibold text-[#AC5D45] whitespace-nowrap">€ {(t.totals?.total_pay ?? 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right text-xs text-[#635F59] truncate max-w-[120px]" title={t.filename || ""}>
                    {(() => {
                      const fn = t.filename || "—";
                      // Strip the long "apprilevazionepresenze-" prefix and dates for cleaner display
                      const short = fn
                        .replace(/^apprilevazionepresenze-?/i, "")
                        .replace(/timbrature-?/i, "")
                        .replace(/dipendente-?/i, "")
                        .replace(/^-+/, "");
                      return short.length > 18 ? short.slice(0, 18) + "…" : short;
                    })()}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <div className="inline-flex gap-0">
                      <Button size="icon" variant="ghost" onClick={() => viewTimesheet(t.id)} title="Visualizza dettaglio" data-testid={`ts-view-${t.id}`} className="h-8 w-8"><Eye className="w-4 h-4 text-[#1E3A5F]" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => downloadTs(t.id, "xlsx")} title="Scarica Excel" data-testid={`ts-xlsx-${t.id}`} className="h-8 w-8"><FileSpreadsheet className="w-4 h-4 text-emerald-700" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => downloadTs(t.id, "csv")} title="Scarica CSV" data-testid={`ts-csv-${t.id}`} className="h-8 w-8"><FileText className="w-4 h-4 text-sky-700" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => downloadTs(t.id, "pdf")} title="Scarica PDF" data-testid={`ts-pdf-${t.id}`} className="h-8 w-8"><FileDown className="w-4 h-4 text-[#AC5D45]" /></Button>
                      <Button size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50 h-8 w-8" onClick={() => deleteTimesheet(t.id)} data-testid={`ts-del-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="emp-dialog">
          <DialogHeader>
            <DialogTitle>{editing.id ? "Modifica dipendente" : "Nuovo dipendente"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 mt-2">
            <div>
              <Label>Nome *</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="emp-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ruolo</Label>
                <Input value={editing.role || ""} onChange={(e) => setEditing({ ...editing, role: e.target.value })} placeholder="Pulizie" data-testid="emp-role" />
              </div>
              <div>
                <Label>Strutture (per filtro timbrature)</Label>
                <div className="text-[10px] text-[#635F59] mt-1 mb-2">
                  Spunta le strutture per cui questo dipendente lavora. Nel file Excel, le righe coi clienti corrispondenti saranno incluse.
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="emp-property-chips">
                  {properties.length === 0 ? (
                    <span className="text-xs text-[#635F59] italic">Nessuna struttura disponibile.</span>
                  ) : properties.map((p) => {
                    const kw = p.client_filter_keyword || p.name.split(" ").pop();
                    // Case-insensitive "is selected" check so legacy uppercase
                    // values (e.g. "GABBIANO") still light up the chip.
                    const cur = editing.client_filters || [];
                    const isOn = cur.some((x) => String(x).trim().toLowerCase() === String(kw).trim().toLowerCase());
                    const tone = propertyTone(p);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          // When toggling OFF, remove ALL case-variants of the keyword
                          // (so a legacy "GABBIANO" entry gets cleaned up too).
                          const lcKw = String(kw).trim().toLowerCase();
                          const next = isOn
                            ? cur.filter((x) => String(x).trim().toLowerCase() !== lcKw)
                            : [...cur.filter((x) => String(x).trim().toLowerCase() !== lcKw), kw];
                          setEditing({ ...editing, client_filters: next });
                        }}
                        data-testid={`emp-chip-${p.slug || p.id}`}
                        className={`px-3 py-1.5 rounded-md text-sm font-semibold border transition ${isOn ? "text-white" : "bg-white hover:bg-[#FDFBF7]"}`}
                        style={isOn
                          ? { backgroundColor: tone, borderColor: tone }
                          : { color: tone, borderColor: tone }}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Optional manual keyword (legacy / extra) */}
            <div>
              <Label className="text-xs">Filtro testuale extra (opzionale)</Label>
              <Input
                value={editing.client_filter || ""}
                onChange={(e) => setEditing({ ...editing, client_filter: e.target.value })}
                placeholder="Es. Emmanuel · usato come fallback"
                data-testid="emp-client"
                className="h-9 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tariffa feriale (€/h)</Label>
                <Input type="number" step="0.5" value={editing.hourly_rate_weekday} onChange={(e) => setEditing({ ...editing, hourly_rate_weekday: e.target.value })} data-testid="emp-rate-w" />
              </div>
              <div>
                <Label>Tariffa festiva (€/h)</Label>
                <Input type="number" step="0.5" value={editing.hourly_rate_holiday} onChange={(e) => setEditing({ ...editing, hourly_rate_holiday: e.target.value })} data-testid="emp-rate-h" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} id="emp-active" data-testid="emp-active" />
              <Label htmlFor="emp-active">Attivo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={saveEmployee} data-testid="emp-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"><Save className="w-4 h-4 mr-2" />Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPLOAD DIALOG */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) { setPreview(null); setUploadFile(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="emp-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" /> Importa timbrature ·{" "}
              <span className="text-[#AC5D45]">{empName(uploadEmpId)}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 text-xs text-[#635F59]">
              Carica il file <strong>apprilevazionepresenze.xlsx</strong> esportato dall&apos;app di
              timbrature. Le ore della domenica e delle festività italiane saranno applicate
              automaticamente con la tariffa festiva.
            </div>

            {/* Selettore dipendente — utile quando apri da "+ Importa Excel/CSV" */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#635F59]">Dipendente *</Label>
                <Select value={uploadEmpId} onValueChange={(v) => { setUploadEmpId(v); setPreview(null); }}>
                  <SelectTrigger data-testid="emp-upload-employee"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} {e.role ? `· ${e.role}` : ""} (€{Number(e.hourly_rate_weekday).toFixed(0)}/€{Number(e.hourly_rate_holiday).toFixed(0)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#635F59]">Struttura (filtro file Excel)</Label>
                <Select
                  value={uploadPropertyId || "auto"}
                  onValueChange={(v) => { setUploadPropertyId(v); setPreview(null); }}
                >
                  <SelectTrigger data-testid="emp-upload-property"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (dalle strutture del dipendente)</SelectItem>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: propertyTone(p) }} />
                          {p.name}
                        </span>
                      </SelectItem>
                    ))}
                    <SelectItem value="none">Nessun filtro (tutti i clienti)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#635F59] mt-1">
                  Forza il filtro a una struttura specifica oppure disattiva il filtro.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-center">
              <Input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="emp-upload-file"
              />
              <Button onClick={doPreview} disabled={!uploadFile || parsing} data-testid="emp-upload-parse" className="bg-[#1E3A5F] hover:bg-[#152C49] text-white">
                {parsing ? "Analisi..." : "Analizza"}
              </Button>
              <Button onClick={doSave} disabled={!preview || saving} data-testid="emp-upload-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}
              </Button>
            </div>

            {preview && (
              <div data-testid="emp-preview">
                {/* ===== Empty due to client filter — let user re-parse ===== */}
                {(preview.totals?.entries_count || 0) === 0 && (preview.skipped_other_client_rows || 0) > 0 && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 my-3" data-testid="emp-preview-filter-mismatch">
                    <div className="flex items-start gap-2">
                      <BadgeEuro className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="font-semibold text-amber-900 text-sm">
                          Nessuna riga corrisponde al filtro &quot;{typeof preview.client_filter === "string" ? preview.client_filter : (preview.client_filter?.name || "—")}&quot;
                        </div>
                        <div className="text-xs text-amber-800 mt-1">
                          Il file contiene {preview.skipped_other_client_rows} righe, ma sono tutte per un altro cliente.
                          Scegli un cliente dal file oppure disattiva il filtro:
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {(preview.detected_clients || []).map((c) => (
                            <Button
                              key={c.name}
                              size="sm"
                              variant="outline"
                              onClick={() => doPreview(c.name)}
                              data-testid={`emp-reparse-client-${c.name.replace(/\W+/g, "-")}`}
                              className="border-amber-400 text-amber-900 hover:bg-amber-100"
                            >
                              Usa &quot;{c.name}&quot; ({c.rows} righe)
                            </Button>
                          ))}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => doPreview("")}
                            data-testid="emp-reparse-no-filter"
                            className="border-amber-400 text-amber-900 hover:bg-amber-100"
                          >
                            Disattiva filtro (tutti i clienti)
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 my-3">
                  <Card icon={<CalendarRange className="w-4 h-4" />} label="Periodo" value={`${preview.period_start || "—"}→${preview.period_end || "—"}`} small />
                  <Card icon={<Clock className="w-4 h-4" />} label="Ore feriali" value={`${preview.totals.hours_weekday.toFixed(2)} h`} />
                  <Card icon={<Clock className="w-4 h-4" />} label="Ore festive" value={`${preview.totals.hours_holiday.toFixed(2)} h`} highlight />
                  <Card icon={<Calculator className="w-4 h-4" />} label="Ore totali" value={`${preview.totals.hours_total.toFixed(2)} h`} />
                  <Card icon={<BadgeEuro className="w-4 h-4" />} label="Paga totale" value={`€ ${preview.totals.total_pay.toFixed(2)}`} highlight />
                </div>

                {/* EXPORT BUTTONS (preview, before save) */}
                <div className="flex flex-wrap items-center gap-2 mb-3 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3">
                  <Download className="w-4 h-4 text-[#1E3A5F]" />
                  <span className="text-xs font-semibold text-[#1E3A5F]">Esporta anteprima:</span>
                  <Button size="sm" variant="outline" onClick={() => downloadPreview("xlsx")} data-testid="emp-preview-xlsx" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadPreview("csv")} data-testid="emp-preview-csv" className="border-sky-300 text-sky-700 hover:bg-sky-50">
                    <FileText className="w-3.5 h-3.5 mr-1" />CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadPreview("pdf")} data-testid="emp-preview-pdf" className="border-[#AC5D45] text-[#AC5D45] hover:bg-[#FDF1E6]">
                    <FileDown className="w-3.5 h-3.5 mr-1" />PDF
                  </Button>
                  <span className="text-[10px] text-[#635F59] ml-auto italic">Puoi scaricare anche senza salvare in archivio</span>
                </div>

                {/* Edit hint */}
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-2.5 mb-2 flex items-start gap-2 text-xs text-sky-900">
                  <EditIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div>
                    <b>Modifica:</b> clicca su un campo <b>Inizio</b>/<b>Fine</b> per correggere errori di battitura,
                    oppure usa il bottone 🗑️ a destra per <b>eliminare righe doppie o sbagliate</b>. Ore, paga e totali si ricalcolano automaticamente.
                    Le righe con <span className="bg-rose-100 px-1 rounded">valore anomalo</span> sono evidenziate in rosso.
                  </div>
                </div>

                <div className="rounded-lg border border-[#E8E2D9] overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Giorno</TableHead>
                        <TableHead>Inizio</TableHead>
                        <TableHead>Fine</TableHead>
                        <TableHead className="text-right">Ore</TableHead>
                        <TableHead>Festivo</TableHead>
                        <TableHead className="text-right">€/h</TableHead>
                        <TableHead className="text-right">Paga</TableHead>
                        <TableHead>Note</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.entries.map((e, i) => {
                        const isAnomaly = !!(e.note && /valore anomalo/.test(e.note));
                        return (
                        <TableRow key={`${e.date || "nd"}-${i}`} className={`${e.is_holiday ? "bg-amber-50/60" : ""} ${isAnomaly ? "bg-rose-50/60" : ""}`}>
                          <TableCell className="text-xs">{e.date}</TableCell>
                          <TableCell className="text-xs">{e.weekday}</TableCell>
                          <TableCell className="text-xs p-1">
                            <input
                              type="time"
                              value={e.start || ""}
                              onChange={(ev) => editPreviewEntry(i, "start", ev.target.value)}
                              data-testid={`prev-start-${i}`}
                              className={`w-[90px] px-1.5 py-1 rounded border text-xs font-mono cursor-pointer hover:border-[#AC5D45] focus:border-[#AC5D45] focus:outline-none focus:ring-1 focus:ring-[#AC5D45] ${isAnomaly ? "border-rose-400 bg-rose-50" : "border-[#E8E2D9] bg-white"}`}
                            />
                          </TableCell>
                          <TableCell className="text-xs p-1">
                            <input
                              type="time"
                              value={e.end || ""}
                              onChange={(ev) => editPreviewEntry(i, "end", ev.target.value)}
                              data-testid={`prev-end-${i}`}
                              className={`w-[90px] px-1.5 py-1 rounded border text-xs font-mono cursor-pointer hover:border-[#AC5D45] focus:border-[#AC5D45] focus:outline-none focus:ring-1 focus:ring-[#AC5D45] ${isAnomaly ? "border-rose-400 bg-rose-50" : "border-[#E8E2D9] bg-white"}`}
                            />
                          </TableCell>
                          <TableCell className="text-right text-xs font-medium">{e.hours.toFixed(2)}</TableCell>
                          <TableCell>{e.is_holiday && <Badge className="bg-amber-100 text-amber-800 border-amber-300" variant="outline">Sì</Badge>}</TableCell>
                          <TableCell className="text-right text-xs">€ {e.rate.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-xs font-semibold">€ {e.pay.toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-[#635F59]">
                            {isAnomaly && <span className="text-rose-700 font-semibold">⚠ </span>}
                            {e.note || ""}
                          </TableCell>
                          <TableCell className="p-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => deletePreviewEntry(i)}
                              data-testid={`prev-del-${i}`}
                              title="Elimina timbro"
                              className="h-7 w-7 hover:bg-rose-50"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                      {/* TOTALE row */}
                      <TableRow className="bg-[#1E3A5F] text-white hover:bg-[#1E3A5F]">
                        <TableCell colSpan={4} className="font-bold text-white">TOTALE</TableCell>
                        <TableCell className="text-right font-bold text-white">{preview.totals.hours_total.toFixed(2)} h</TableCell>
                        <TableCell className="text-xs text-amber-200">
                          {preview.totals.hours_holiday > 0 && <>fest. {preview.totals.hours_holiday.toFixed(2)} h</>}
                        </TableCell>
                        <TableCell colSpan={2} className="text-right font-display text-lg text-white">€ {preview.totals.total_pay.toFixed(2)}</TableCell>
                        <TableCell colSpan={2} className="text-[10px] text-amber-100">
                          fer. €{preview.totals.pay_weekday.toFixed(2)} · fes. €{preview.totals.pay_holiday.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* VIEW SAVED TIMESHEET DIALOG */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="ts-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" /> Dettaglio timbratura ·{" "}
              <span className="text-[#AC5D45]">{viewing?.employee_name || "—"}</span>
              <span className="text-xs text-[#635F59] ml-2 font-normal">
                {viewing?.period_start || "—"} → {viewing?.period_end || "—"}
              </span>
            </DialogTitle>
          </DialogHeader>

          {viewing && (
            <div className="space-y-3">
              {/* SUMMARY CARDS */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card icon={<CalendarRange className="w-4 h-4" />} label="Periodo" value={`${viewing.period_start || "—"}→${viewing.period_end || "—"}`} small />
                <Card icon={<Clock className="w-4 h-4" />} label="Ore feriali" value={`${(viewing.totals?.hours_weekday ?? 0).toFixed(2)} h`} />
                <Card icon={<Clock className="w-4 h-4" />} label="Ore festive" value={`${(viewing.totals?.hours_holiday ?? 0).toFixed(2)} h`} highlight />
                <Card icon={<Calculator className="w-4 h-4" />} label="Ore totali" value={`${(viewing.totals?.hours_total ?? 0).toFixed(2)} h`} />
                <Card icon={<BadgeEuro className="w-4 h-4" />} label="Paga totale" value={`€ ${(viewing.totals?.total_pay ?? 0).toFixed(2)}`} highlight />
              </div>

              {/* DOWNLOAD BUTTONS */}
              <div className="flex flex-wrap items-center gap-2 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3">
                <Download className="w-4 h-4 text-[#1E3A5F]" />
                <span className="text-xs font-semibold text-[#1E3A5F]">Scarica:</span>
                <Button size="sm" variant="outline" onClick={() => downloadTs(viewing.id, "xlsx")} data-testid="ts-view-xlsx" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadTs(viewing.id, "csv")} data-testid="ts-view-csv" className="border-sky-300 text-sky-700 hover:bg-sky-50">
                  <FileText className="w-3.5 h-3.5 mr-1" />CSV
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadTs(viewing.id, "pdf")} data-testid="ts-view-pdf" className="border-[#AC5D45] text-[#AC5D45] hover:bg-[#FDF1E6]">
                  <FileDown className="w-3.5 h-3.5 mr-1" />PDF
                </Button>
                <span className="text-[10px] text-[#635F59] ml-auto italic">
                  Tariffe applicate: feriale €{Number(viewing.hourly_rate_weekday).toFixed(2)}/h · festiva €{Number(viewing.hourly_rate_holiday).toFixed(2)}/h
                </span>
              </div>

              {/* Edit hint + Save changes button */}
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-2.5 flex items-center gap-2 text-xs text-sky-900">
                <EditIcon className="w-3.5 h-3.5 shrink-0" />
                <span>
                  <b>Modifica:</b> clicca su <b>Inizio</b>/<b>Fine</b> per correggere o usa 🗑️ per <b>eliminare righe doppie/sbagliate</b>. Premi <b>Salva modifiche</b> per persistere.
                </span>
                <Button
                  size="sm"
                  onClick={saveViewChanges}
                  data-testid="ts-view-save"
                  className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Save className="w-3.5 h-3.5 mr-1" />Salva modifiche
                </Button>
              </div>

              {/* ENTRIES TABLE */}
              <div className="rounded-lg border border-[#E8E2D9] overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Giorno</TableHead>
                      <TableHead>Inizio</TableHead>
                      <TableHead>Fine</TableHead>
                      <TableHead className="text-right">Ore</TableHead>
                      <TableHead>Festivo</TableHead>
                      <TableHead className="text-right">€/h</TableHead>
                      <TableHead className="text-right">Paga</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(viewing.entries || []).map((e, i) => {
                      const isAnomaly = !!(e.note && /valore anomalo/.test(e.note));
                      return (
                      <TableRow key={`${e.date || "nd"}-${i}`} className={`${e.is_holiday ? "bg-amber-50/60" : ""} ${isAnomaly ? "bg-rose-50/60" : ""}`}>
                        <TableCell className="text-xs">{e.date}</TableCell>
                        <TableCell className="text-xs">{e.weekday}</TableCell>
                        <TableCell className="text-xs p-1">
                          <input
                            type="time"
                            value={e.start || ""}
                            onChange={(ev) => editViewEntry(i, "start", ev.target.value)}
                            data-testid={`view-start-${i}`}
                            className={`w-[90px] px-1.5 py-1 rounded border text-xs font-mono cursor-pointer hover:border-[#AC5D45] focus:border-[#AC5D45] focus:outline-none focus:ring-1 focus:ring-[#AC5D45] ${isAnomaly ? "border-rose-400 bg-rose-50" : "border-[#E8E2D9] bg-white"}`}
                          />
                        </TableCell>
                        <TableCell className="text-xs p-1">
                          <input
                            type="time"
                            value={e.end || ""}
                            onChange={(ev) => editViewEntry(i, "end", ev.target.value)}
                            data-testid={`view-end-${i}`}
                            className={`w-[90px] px-1.5 py-1 rounded border text-xs font-mono cursor-pointer hover:border-[#AC5D45] focus:border-[#AC5D45] focus:outline-none focus:ring-1 focus:ring-[#AC5D45] ${isAnomaly ? "border-rose-400 bg-rose-50" : "border-[#E8E2D9] bg-white"}`}
                          />
                        </TableCell>
                        <TableCell className="text-right text-xs font-medium">{Number(e.hours || 0).toFixed(2)}</TableCell>
                        <TableCell>{e.is_holiday && <Badge className="bg-amber-100 text-amber-800 border-amber-300" variant="outline">Sì</Badge>}</TableCell>
                        <TableCell className="text-right text-xs">€ {Number(e.rate || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right text-xs font-semibold">€ {Number(e.pay || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-xs text-[#635F59]">
                          {isAnomaly && <span className="text-rose-700 font-semibold">⚠ </span>}
                          {e.note || ""}
                        </TableCell>
                        <TableCell className="p-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => deleteViewEntry(i)}
                            data-testid={`view-del-${i}`}
                            title="Elimina timbro"
                            className="h-7 w-7 hover:bg-rose-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {/* TOTALE row */}
                    <TableRow className="bg-[#1E3A5F] text-white hover:bg-[#1E3A5F]">
                      <TableCell colSpan={4} className="font-bold text-white">TOTALE</TableCell>
                      <TableCell className="text-right font-bold text-white">{(viewing.totals?.hours_total ?? 0).toFixed(2)} h</TableCell>
                      <TableCell className="text-xs text-amber-200">
                        {(viewing.totals?.hours_holiday ?? 0) > 0 && <>fest. {viewing.totals.hours_holiday.toFixed(2)} h</>}
                      </TableCell>
                      <TableCell colSpan={2} className="text-right font-display text-lg text-white">€ {(viewing.totals?.total_pay ?? 0).toFixed(2)}</TableCell>
                      <TableCell colSpan={2} className="text-[10px] text-amber-100">
                        fer. €{(viewing.totals?.pay_weekday ?? 0).toFixed(2)} · fes. €{(viewing.totals?.pay_holiday ?? 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {viewing.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 italic">
                  <b>Note:</b> {viewing.notes}
                </div>
              )}
              {viewing.filename && (
                <div className="text-[10px] text-[#635F59]">
                  File originale: <code>{viewing.filename}</code>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Card = ({ icon, label, value, highlight, small }) => (
  <div className={`border rounded-lg p-3 ${highlight ? "bg-[#AC5D45]/5 border-[#AC5D45]/30" : "bg-white border-[#E8E2D9]"}`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#635F59]">{icon}{label}</div>
    <div className={`mt-1 font-display ${small ? "text-sm" : "text-xl"} ${highlight ? "text-[#AC5D45]" : "text-[#1E3A5F]"}`}>{value}</div>
  </div>
);
